import { CSSProperties, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Colors, Sizes } from '@/components/ui/types';

import { RadialProgressProps } from './types';

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'text-neutral',
  primary: 'text-primary',
  secondary: 'text-secondary',
  accent: 'text-accent',
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
};

const sizeMap: Record<Sizes, string> = {
  xs: '2.8rem',
  sm: '4rem',
  md: '5rem',
  lg: '7rem',
  xl: '10rem',
};

const thicknessMap: Record<Sizes, string> = {
  xs: '5px',
  sm: '6px',
  md: '8px',
  lg: '10px',
  xl: '12px',
};

export const RadialProgress = forwardRef<HTMLDivElement, RadialProgressProps>(
  (
    {
      value,
      size = 'md',
      thickness = 'md',
      color = 'default',
      classNames,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const classes = twMerge(
      'radial-progress',
      colorClasses[color],
      classNames?.track && 'isolate',
      classNames?.base,
    );

    const sizeStyle = {
      '--size': sizeMap[size],
      ...(thickness ? { '--thickness': thicknessMap[thickness] } : {}),
    } as CSSProperties;

    const mergedStyle = {
      '--value': value,
      ...sizeStyle,
      ...style,
    } as CSSProperties;

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
        className={classes}
        style={mergedStyle}
        {...props}
      >
        <ConditionalRenderer condition={!!classNames?.track}>
          <span
            aria-hidden
            className={twMerge(
              'radial-progress absolute inset-0 -z-10',
              classNames?.track,
            )}
            style={{ '--value': 100, ...sizeStyle } as CSSProperties}
          />
        </ConditionalRenderer>
        {children}
      </div>
    );
  },
);

RadialProgress.displayName = 'RadialProgress';
